import CustomAsyncMultiSelect from 'Components/Common/CustomAsyncMultiSelect';
import CustomMultiSelect from 'Components/Common/CustomMultiSelect';
import RadioTileGroup from 'Components/input/RadioTileGroup/RadioTileGroup';
import { genders } from 'constants/campaign';
import { FIELDS_INSTRUCTIONS } from 'constants/fieldsInstructions';
import { PRIVILEGE } from 'constants/privilege';
import { useFormik } from 'formik';
import { usePrivileges } from 'hooks/useAuth';
import CampaignCreationLayout from 'Layouts/CampaignCreationLayout';
import { debounce } from 'lodash';
import Error404 from 'pages/Error/Error404';
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Col, FormFeedback, Input, Row, Spinner } from 'reactstrap';
import { fetchCities, updateCampaignRequirement } from 'services/api/campaign';
import { fetchRegionsList } from 'slices/master/thunk';
import { AppDispatch, RootState } from 'store';
import { isset, scrollToElement } from 'utils/helpers';
import CreatableTags from 'Components/Common/CreatableTags';
import * as Yup from "yup";
import { toast } from 'react-toastify';
import { ScreeningQuestion as ScreeningQuestionInterface, ScreeningQuestionType, UnsavedScreeningQuestion } from 'types/campaign/details';
import withAccessControl from 'hoc/withAccessControl';
import ScreeningQuestion from 'Components/campaigns/create-edit/ScreeningQuestion';
import { fetchCampaignScreeningQuestionsThunk } from 'slices/campaigns/details/thunk';
const applicationRestrictionOptions = {
    allow: 'No Restrictions',
    restrict_ongoing_collab: 'Restrict Ongoing Collab',
    restrict_completed_collab: 'Restrict Any Collab'
}
const CampaignCreators = () => {


    const { campaignId } = useParams();

    const configurations = useSelector((state: RootState) => state.auth.configurations);
    const privileges = usePrivileges();

    const campaignRequirement = useSelector((state: RootState) => state.campaignRequirements.items[campaignId!])
    const campaignDetails = useSelector((state: RootState) => state.campaignDetails.campaigns[(campaignId ?? 'create')])

    const selectedAgency = useSelector((state: RootState) => state.auth.selectedAgency);
    const niches = useSelector((state: RootState) => state.auth.master?.niche_list);
    const specialityNiches = useSelector((state: RootState) => state.auth.master?.speciality_niches);

    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const regions = useSelector((state: RootState) => state.master.regions.value);
    const languages = useSelector((state: RootState) => state.master.languages);
    const age_groups = useSelector((state: RootState) => state.auth.master?.age_group_list);
    const [locationScope, setLocationScope] = useState<string | null>(null);
    const [searchParams] = useSearchParams();
    const validate = searchParams.get('validate');
    const [validatedOnce, setValidatedOnce] = useState(false);
    useEffect(() => {
        if (!campaignRequirement || locationScope) return;
        let location_scope = 'city';
        if ((campaignRequirement?.campaign_requirement?.requirement?.details?.regions ?? []).length > 0) {
            location_scope = 'state';
        }
        setLocationScope(location_scope);
    }, [campaignRequirement, locationScope]);

    const screeningQuestionsData = useSelector((state: RootState) => state.campaignDetails.screeningQuestions[campaignId!]);
    const [screeningQuestions, setScreeningQuestions] = useState<Array<ScreeningQuestionInterface | UnsavedScreeningQuestion>>(screeningQuestionsData?.questions || []);

    useEffect(() => {
        setScreeningQuestions(screeningQuestionsData?.questions || []);
    }, [screeningQuestionsData]);

    const creatorsCount = !isNaN(Number(campaignRequirement?.campaign_requirement?.requirement?.details?.creator_count?.max)) ? Number(campaignRequirement?.campaign_requirement?.requirement?.details?.creator_count?.max) : null;

    const [creatorsCountRange, setCreatorsCountRange] = useState(creatorsCount ? (
        creatorsCount <= 5 ? '<5' : creatorsCount <= 10 ? '5-10' : creatorsCount <= 20 ? '10-20' : '>20'
    ) : '');

    useEffect(() => {
        if (!creatorsCount || creatorsCount < 5) {
            setCreatorsCountRange('');
            return;
        }
        setCreatorsCountRange(creatorsCount ? (
            creatorsCount <= 5 ? '<5' : creatorsCount <= 10 ? '5-10' : creatorsCount <= 20 ? '10-20' : '>20'
        ) : '')
    }, [creatorsCount])




    useEffect(() => {
        if (campaignId) {
            dispatch(fetchCampaignScreeningQuestionsThunk(campaignId));
        }
    }, [campaignId])

    const [loading, setLoading] = useState(false);

    const formFields = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            ...campaignRequirement?.campaign_requirement?.requirement?.details,
            creator_count: campaignRequirement?.campaign_requirement?.requirement?.details?.creator_count || undefined,
            engagement_score: campaignRequirement?.campaign_requirement?.requirement?.details?.min_engagement_score || undefined,
            creator_lookalikes: campaignRequirement?.campaign_requirement?.requirement?.details?.creator_lookalikes || [],
            platform: campaignRequirement?.campaign_requirement?.campaign?.platform
        },
        validationSchema: Yup.object({
            creator_count: Yup.object({
                max: Yup.number().required("Please enter estimated number of creators to hire").min(5, 'Please enter estimated number of creators to hire')
            }),
            gender: Yup.string().required("Please select influencer's gender"),
            speciality_niches: Yup.array().required().min(1, "Please select atleast one niche").max(3, "Maximum 3 niches can be selected"),
            follower_count: Yup.object({
                min: Yup.number().required("Please enter minimum number of followers/subscribers").min(1, "Please enter minimum number of followers/subscribers")
            }),
            engagement_score: Yup.object({
                min: Yup.number()
                    .transform((value, originalValue) =>
                        originalValue === "" || isNaN(originalValue) ? null : value
                    )
                    .nullable()
                    .max(100, "Engagement rate cannot be more than 100%"),
            }),

            age: Yup.object({
                min: Yup.number().nullable(),
                max: Yup.number().nullable(),
            }).test(
                'min-max-required-together',
                'Please set minimum and maximum age range.',
                function (value) {
                    const { min, max } = value || {};
                    if ((min && !max) || (max && !min)) {
                        return false;
                    }
                    return true;
                }
            ),
        }),
        onSubmit: async (campaignData) => {
            if (!privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)) {
                return toast.error("You don't have permission to perform this action");
            }
            if (!campaignId || !selectedAgency?.uuid) {
                return;
            }
            const creatorsCountRangeMin = creatorsCountRange === '<5' ? 0 : creatorsCountRange === '5-10' ? 5 : creatorsCountRange === '10-20' ? 10 : 20;
            if ((campaignData.creator_count?.max ?? 0) < creatorsCountRangeMin) {
                return toast.error(`Please enter estimated number of creators to hire`);
            }
            try {
                setLoading(true);
                await updateCampaignRequirement(selectedAgency?.uuid!, campaignId, campaignData);
                navigate(`/campaign/${campaignId}/deliverables`);
            } catch (error) {
                toast.error("An error occurred while updating the campaign");
            } finally {
                setLoading(false);
            }
        }
    });

    useEffect(() => {
        setTimeout(() => {
            if (!validatedOnce && validate === 'true' && formFields) {
                const fieldNames = Object.keys(formFields.initialValues);
                const touchedFields = fieldNames.reduce((acc, name) => {
                    acc[name] = true;
                    return acc;
                }, {} as Record<string, boolean>);

                formFields.setTouched(touchedFields, true);

                formFields.validateForm().then(errors => {
                    formFields.setErrors(errors);
                    setValidatedOnce(true);
                });
            }
        }, 1000)
    }, [validate, validatedOnce, formFields]);

    const _loadCitiesSuggestions = (query: string, callback: any) => {
        if (!query || query.length < 3) callback([]);

        fetchCities(query)
            .then(resp => callback(resp.map((item: string) => ({ value: item, label: item })))).catch(() => callback([]))
    };

    const loadCitiesSuggestions = debounce(_loadCitiesSuggestions, 200);

    useEffect(() => {
        dispatch(fetchRegionsList());
    }, [dispatch])

    const showSpecialityNiches = useMemo(() => {
        if (!Array.isArray(formFields.values.niches) || formFields.values.niches.length === 0) return true;
        return false;
    }, [formFields])


    useEffect(() => {
        if (creatorsCountRange === '') return;
        if (creatorsCountRange === '>20') {
            formFields.setFieldValue('creator_count', { max: creatorsCount ?? '' });
        } else {
            const count = creatorsCountRange === '<5' ? 5 : creatorsCountRange === '5-10' ? 10 : 20;
            formFields.setFieldValue('creator_count', { max: count });
        }
    }, [creatorsCountRange, creatorsCount])

    useEffect(() => {
        const errorField = Object.keys(formFields?.errors ?? {})?.[0];
        if (errorField) {
            setTimeout(() => {
                scrollToElement('error-' + errorField)
            }, 10)
        }
    }, [formFields.errors])

    useEffect(() => {
        if (!isset(formFields.values.application_restriction_duration) && formFields.values.application_restriction !== 'allow') {
            formFields.setFieldValue('application_restriction_duration', "2");
        }
    }, [formFields])

    if (!campaignId) return <Error404 />

    return (
        <CampaignCreationLayout
            activeStep='creators'
            actionRow={
                <div className="d-flex justify-content-between mb-4">
                    <div></div>
                    <button type='button' onClick={() => {
                        formFields.handleSubmit();
                    }} disabled={loading || !privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)} className="theme-btn theme-btn-lg btn-gradient form-continue-btn d-flex justify-content-between align-items-center">
                        <React.Fragment>
                            {
                                'Proceed to Next'
                            }
                            {
                                loading ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : <i className='bx fs-4 bx-right-arrow-alt' ></i>
                            }
                        </React.Fragment>
                    </button>
                </div>
            }
        >
            <div className="form-group">
                <div className="d-flex flex-column">
                    <label htmlFor="creators-count-range">Creators <span className="text-danger">*</span></label>
                    <span className='field-desc'>How many creators do you want to hire?</span>
                </div>
                <RadioTileGroup
                    options={[
                        { id: '<5', label: '<5' },
                        { id: '5-10', label: '5-10' },
                        { id: '10-20', label: '10-20' },
                        { id: '>20', label: '>20' },

                    ]}
                    name='creators-count-range'
                    defaultValue={creatorsCountRange}
                    onChange={(value) => {
                        setCreatorsCountRange(value);
                    }}
                    selectFirstWhenNoValueProvided={false}
                />

            </div>
            {
                creatorsCountRange === '>20' && (
                    <div className="mt-4">
                        <input type="number" onBlur={() => formFields.setFieldTouched('creator_count', true)} className='form-control' placeholder='Enter an estimated number of creators' value={formFields.values.creator_count?.max || ''} onChange={(e) => formFields.setFieldValue('creator_count', { max: e.target.value ? parseInt(e.target.value) : '' })} />
                    </div>
                )
            }
            {
                formFields.touched.creator_count && formFields.errors.creator_count &&
                <FormFeedback id={'error-creator_count'} className="d-block">
                    {(((formFields.errors.creator_count as any)?.max) ?? (typeof formFields.errors.creator_count === 'string' ? formFields.errors.creator_count : '')) as string}
                </FormFeedback>
            }
            <div className="campaign-sub-form">
                <Row className='mt-4 gap-3 gap-lg-0'>
                    <Col lg={6} className="d-flex">
                        <div className="form-group w-100">
                            <label htmlFor="niches">Niches <span className="text-danger">*</span></label>
                            {
                                showSpecialityNiches ? (
                                    <CustomMultiSelect
                                        placeholder="Select Niches"
                                        menuPlacement='top'
                                        isMulti
                                        styles={{
                                            multiValueLabel: (base: any) => {
                                                return { ...base, color: "white" }
                                            }
                                        }}
                                        onBlur={() => formFields.setFieldTouched('speciality_niches', true)}
                                        options={(specialityNiches ?? []).map((niche) => ({label: niche.name, value: niche.id}))}
                                        classNamePrefix={'influns'}
                                        values={formFields.values?.speciality_niches ?? []}
                                        onChange={(selectedNiches) => {
                                            formFields.setFieldValue('speciality_niches', selectedNiches);
                                        }}
                                    />
                                ) : (
                                    <CustomMultiSelect
                                        placeholder="Select Niches"
                                        menuPlacement='top'
                                        isMulti
                                        styles={{
                                            multiValueLabel: (base: any) => {
                                                return { ...base, color: "white" }
                                            }
                                        }}
                                        onBlur={() => formFields.setFieldTouched('niches', true)}
                                        options={Object.entries((niches ?? {})).map(([id, name]) => ({ label: name, value: id }))}
                                        classNamePrefix={'influns'}
                                        values={formFields.values?.niches ?? []}
                                        onChange={(selectedNiches) => {
                                            formFields.setFieldValue('niches', selectedNiches);
                                        }}
                                    />
                                )
                            }

                            {formFields.touched.speciality_niches && formFields.errors.speciality_niches && <FormFeedback id='error-speciality_niches' className='d-block'>{formFields.errors.speciality_niches}</FormFeedback>}
                        </div>
                    </Col>
                    <Col lg={6}>
                        <div className="form-group">
                            <label htmlFor="gender">Minimum Followers/Subscribers <span className="text-danger">*</span></label>
                            <Input
                                type='text'
                                name='follower_count'
                                className='form-control'
                                onChange={(e) => formFields.setFieldValue('follower_count', { min: parseInt(e.target.value ? e.target.value : '0') })}
                                onBlur={() => formFields.setFieldTouched('follower_count', true, true)}
                                value={formFields?.values?.follower_count?.min ? formFields.values.follower_count.min : ''}
                                invalid={formFields.touched.follower_count?.min && formFields.errors.follower_count?.min ? true : false}
                                placeholder='Minimum Followers/Subscribers' />
                        </div>
                        {formFields.touched.follower_count && formFields.errors.follower_count && <FormFeedback id='error-follower_count' className='d-block'>{formFields.errors.follower_count?.min as string}</FormFeedback>}
                    </Col>
                </Row>
                <Row className='mt-4 gap-3 gap-lg-0'>
                    <Col lg={6} className="d-flex">
                        <div className="form-group w-100">
                            <label htmlFor="location-type">Location Scope</label>
                            <RadioTileGroup
                                name="location-type"
                                options={[{ id: 'city', label: 'City' }, { id: 'state', label: 'State' }]}
                                defaultValue={locationScope ? locationScope : 'city'}
                                onChange={(locationType) => {
                                    setLocationScope(locationType)
                                    if (locationType === 'city') {
                                        formFields.setValues({ ...formFields.values, regions: null, cities: [] });
                                    } else {
                                        formFields.setValues({ ...formFields.values, regions: [], cities: null });
                                    }
                                }}
                            />
                        </div>
                    </Col>
                    <Col lg={6} className='d-flex flex-column justify-content-center'>
                        <div className="form-group w-100">
                            <label htmlFor="location-type">Location</label>
                            {
                                locationScope === 'city' &&
                                <CustomAsyncMultiSelect
                                    placeholder="Select Location"
                                    menuPlacement='top'
                                    cacheOptions
                                    isMulti
                                    loadOptions={loadCitiesSuggestions}
                                    styles={{
                                        multiValueLabel: (base: any) => {
                                            return { ...base, color: "white" }
                                        }
                                    }}
                                    values={formFields?.values?.cities ?? []}
                                    onChange={(selectedCities) => {
                                        formFields.setFieldValue('cities', selectedCities);
                                    }}
                                />
                            }
                            {
                                locationScope === 'state' &&
                                <CustomMultiSelect
                                    placeholder="Select Location"
                                    menuPlacement='top'
                                    isMulti
                                    styles={{
                                        multiValueLabel: (base: any) => {
                                            return { ...base, color: "white" }
                                        }
                                    }}
                                    options={regions.map((region) => ({ label: region, value: region }))}
                                    values={formFields?.values?.regions ?? []}
                                    onChange={(selectedRegions) => {
                                        formFields.setFieldValue('regions', selectedRegions);
                                    }}
                                />
                            }
                        </div>
                    </Col>
                </Row>
                <Row className='mt-4 gap-3 gap-lg-0'>
                    <Col lg={6}>
                        <div className="form-group">
                            <label htmlFor="min-engagement-rate">Minimum Engagement Rate (ER %)</label>
                            <Input
                                type='text'
                                name='min_engagement_score'
                                className='form-control'
                                onChange={(e) => formFields.setFieldValue('engagement_score', { min: parseInt(e.target.value ? e.target.value : '0') })}
                                onBlur={() => formFields.setFieldTouched('engagement_score', true, true)}
                                value={formFields?.values?.engagement_score?.min ? formFields.values.engagement_score.min : ''}
                                placeholder='Minimum Engagement Rate' />
                        </div>
                        {formFields.touched.engagement_score && formFields.errors.engagement_score &&
                            <FormFeedback className='d-block'>
                                {typeof formFields.errors.engagement_score === 'string'
                                    ? formFields.errors.engagement_score
                                    : (formFields.errors.engagement_score as any)?.min}
                            </FormFeedback>
                        }
                    </Col>
                    <Col lg={6}>
                        <div className="form-group">
                            <label htmlFor="languages">Minimum Average Views</label>
                            <Input
                                type='text'
                                name='average_views'
                                className='form-control'
                                onChange={(e) => formFields.setFieldValue('average_views', { min: parseInt(e.target.value ? e.target.value : '0') })}
                                onBlur={() => formFields.setFieldTouched('average_views', true, true)}
                                value={formFields?.values?.average_views?.min ? formFields.values.average_views.min : ''}
                                invalid={formFields.touched.average_views?.min && formFields.errors.average_views?.min ? true : false}
                                placeholder='Minimum Average Views' />
                        </div>
                        {formFields.touched.average_views && formFields.errors.average_views && <FormFeedback className='d-block'>{formFields.errors.average_views?.min as string}</FormFeedback>}
                    </Col>
                </Row>
                <Row className='mt-4 gap-3 gap-lg-0'>
                    <Col lg={6}>
                        <div className="form-group">
                            <label htmlFor="gender">Gender</label>
                            <select onBlur={() => formFields.setFieldTouched('gender', true)} value={formFields.values.gender} onChange={(e) => formFields.setFieldValue('gender', e.target.value)} className='form-select'>
                                {
                                    Object.entries(genders).map(([key, value]) => (
                                        <option key={key} value={key} selected={formFields.values.gender === key}>{value}</option>
                                    ))
                                }
                            </select>
                            {formFields.touched.gender && formFields.errors.gender && <FormFeedback className='d-block'>{formFields.errors.gender}</FormFeedback>}
                        </div>
                    </Col>
                    <Col lg={6}>
                        <div className="form-group">
                            <label htmlFor="languages">Languages</label>
                            <CustomMultiSelect
                                placeholder="Select Language"
                                menuPlacement='top'
                                isMulti
                                styles={{
                                    multiValueLabel: (base: any) => {
                                        return { ...base, color: "white" }
                                    }
                                }}
                                options={Object.entries((languages ?? {})).map(([id, name]) => ({ label: name, value: id }))}
                                values={formFields?.values?.languages ?? []}
                                onChange={(selectedLang) => {
                                    formFields.setFieldValue('languages', selectedLang);
                                }}
                            />
                        </div>
                    </Col>
                </Row>
                <Row className='mt-4 gap-3 gap-lg-0'>
                    <Col lg={6}>
                        <div className="form-group">
                            <label htmlFor="gender">Age Range</label>
                            <div className="d-flex gap-3">
                                <select className="form-select w-50"
                                    value={formFields?.values?.age?.min ?? ''}
                                    onChange={(e) => formFields.setFieldValue('age', { ...formFields.values.age, min: e.target.value })}
                                    onBlur={() => formFields.setFieldTouched('age', true, true)}
                                    name="age-min"
                                >
                                    <option value="">From</option>
                                    {Array.from({ length: 87 }, (_, i) => i + 13).map((age) => {
                                        if (formFields?.values?.age?.max && age > formFields.values.age.max) return null;
                                        return (
                                            <option key={age} value={age}>
                                                {age}
                                            </option>
                                        )
                                    })}
                                </select>
                                <select className="form-select w-50"
                                    value={formFields?.values?.age?.max ?? ''}
                                    onChange={(e) => formFields.setFieldValue('age', { ...formFields.values.age, max: e.target.value })}
                                    onBlur={() => formFields.setFieldTouched('age', true, true)}
                                    name="age-max"
                                >
                                    <option value="">To</option>
                                    {Array.from({ length: 87 }, (_, i) => i + 13).map((age) => {
                                        if (formFields?.values?.age?.min && age < formFields.values.age.min) return null
                                        return (
                                            <option key={age} value={age}>
                                                {age}
                                            </option>
                                        )
                                    })}
                                </select>
                            </div>
                            {formFields.touched.age && formFields.errors.age && <FormFeedback className='d-block'>{formFields.errors.age as string}</FormFeedback>}
                        </div>

                    </Col>
                    <Col lg={6}>
                    </Col>
                </Row>
                {/* <Row className='mt-3 gap-3 gap-lg-0'>
                    <Col lg={12}>
                        <div className="min-engagement-rate-desc">
                            <span>
                                The minimum recommended number of followers is 3000, with an expected ER of at least 1%.
                                You set the values lower, so you may not ensure optimal performance
                                of the campaign
                            </span>
                        </div>
                    </Col>
                </Row> */}
                {/* <Row className='mt-4 gap-3 gap-lg-0'>
                    <Col lg={12}>
                        <button className="advance-filter-button" type="button" data-bs-toggle="collapse" data-bs-target="#creator-advance-filters" aria-expanded="true" aria-controls="creator-advance-filters">
                            <div className='d-flex align-items-center gap-2'>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.99974 1H11.9999C12.5521 1 12.9998 1.44764 12.9999 1.99987L12.9999 2.58569C13 2.85097 12.8946 3.10538 12.707 3.29295L8.62599 7.37377C8.43844 7.56131 8.33307 7.81567 8.33307 8.0809L8.33307 11.7192C8.33307 12.3698 7.72168 12.8472 7.09054 12.6894L6.42387 12.5227C5.9787 12.4114 5.66641 12.0114 5.66641 11.5526L5.66641 8.08088C5.66641 7.81566 5.56105 7.56131 5.37351 7.37377L1.29263 3.29289C1.1051 3.10536 0.999739 2.851 0.999739 2.58579V2C0.999739 1.44772 1.44745 1 1.99974 1Z" stroke="black" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                <span>Advance Filters</span>
                            </div>
                        </button>
                    </Col>
                </Row>
                 */}
            </div>
            {
                campaignDetails?.campaign?.tier && campaignDetails?.campaign?.tier !== 'basic' &&
                <>
                    <div className="divider my-5"></div>
                    <div className="form-group">
                        <div className="d-flex flex-column">
                            <label htmlFor="creators-count-range">Creator's Audience</label>
                        </div>
                    </div>
                    <div className="campaign-sub-form">
                        <Row className='mt-4 gap-3 gap-lg-0'>
                            <Col lg={6}>
                                <div className="form-group">
                                    <label htmlFor="audience-city">City</label>
                                    <CustomAsyncMultiSelect
                                        placeholder="Select Location"
                                        menuPlacement='top'
                                        cacheOptions
                                        isMulti
                                        loadOptions={loadCitiesSuggestions}
                                        styles={{
                                            multiValueLabel: (base: any) => {
                                                return { ...base, color: "white" }
                                            }
                                        }}
                                        values={formFields?.values?.audience_cities ?? []}
                                        onChange={(selectedCities) => {
                                            formFields.setFieldValue('audience_cities', selectedCities);
                                        }}
                                    />
                                </div>

                            </Col>
                            <Col lg={6}>
                                <div className="form-group">
                                    <label htmlFor="audience-gender">Gender</label>
                                    <select value={formFields.values.audience_gender} onChange={(e) => formFields.setFieldValue('audience_gender', e.target.value)} className='form-select'>
                                        {
                                            Object.entries(genders).map(([key, value]) => (
                                                <option key={key} value={key} selected={formFields.values.audience_gender === key}>{value}</option>
                                            ))
                                        }
                                    </select>
                                </div>
                            </Col>
                        </Row>
                        <Row className='mt-4 gap-3 gap-lg-0'>
                            <Col lg={6}>
                                <div className="form-group">
                                    <label htmlFor="audience-age">Age Range</label>
                                    <CustomMultiSelect
                                        placeholder="Select Age Group"
                                        menuPlacement='top'
                                        isMulti
                                        styles={{
                                            multiValueLabel: (base: any) => {
                                                return { ...base, color: "white" }
                                            }
                                        }}
                                        options={(age_groups ?? []).map((age_group) => ({ label: age_group, value: age_group }))}
                                        values={formFields?.values?.audience_age_groups ?? []}
                                        onChange={(selectedAgeGroups) => {
                                            formFields.setFieldValue('audience_age_groups', selectedAgeGroups);
                                        }}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>
                </>
            }
            <div className="divider my-5"></div>
            <div className="form-group">
                <div className="d-flex flex-column">
                    <label htmlFor="creators-count-range">Screening Questions <span className="optional">(Optional)</span></label>
                </div>
            </div>
            <div className="campaign-sub-form">
                {
                    screeningQuestions.length > 0 ? screeningQuestions.map((question: any, index: number) => (
                        <ScreeningQuestion key={index} screeningQuestion={question} campaignId={campaignId} onDeleteUnsaved={() => {
                            let updatedScreeningQuestions = [...screeningQuestions];
                            updatedScreeningQuestions.splice(index, 1);
                            setScreeningQuestions(updatedScreeningQuestions);
                        }} />
                    )) : (
                        <p className='mt-2 mb-0 text-muted'>No screening questions added.</p>
                    )
                }
                <button type="button" className="btn btn-link p-0 mt-3" onClick={() => {
                    setScreeningQuestions([...screeningQuestions, { question: '', type: ScreeningQuestionType.TEXT }]);
                }}>+ Add Question</button>
            </div>
            <div className="divider my-5"></div>
            <div className="form-group">
                <div className="d-flex flex-column">
                    <label htmlFor="creators-count-range">Manage Creators <span className="optional">(Optional)</span></label>
                </div>
            </div>
            <div className="campaign-sub-form">
                <div className="form-group mt-3">
                    <label htmlFor="lookalike">Lookalikes</label>
                    <CreatableTags
                        placeholder="Add link to favourites creator "
                        values={formFields.values.creator_lookalikes ?? []}
                        onChange={(tags) => {
                            formFields.setFieldValue('creator_lookalikes', tags);
                        }}
                    />
                </div>
                <Row className='mt-4 gap-3 gap-lg-0'>
                    <Col lg={12}>
                        <div className="form-group">
                            <div className="d-flex flex-column">
                                <label htmlFor="creators-count-range">Exclude creators from past campaign(s)</label>
                                <span className='field-desc'>{FIELDS_INSTRUCTIONS.CAMPAIGN.APPLICATION_RESTRICTION}</span>
                            </div>
                            <RadioTileGroup
                                name="application_restriction"
                                options={Object.entries(applicationRestrictionOptions).map(([key, value]) => ({ id: key, label: value }))}
                                defaultValue={formFields?.values?.application_restriction ?? 'allow'}
                                onChange={(value) => {
                                    formFields.setValues({
                                        ...formFields.values,
                                        application_restriction: value,
                                        ...(value !== 'restrict_completed_collab' ? { application_restriction_duration: undefined } : {})
                                    });
                                }}
                            />
                        </div>
                    </Col>
                </Row>
                {
                    formFields.values.application_restriction !== 'allow' &&
                    <Row className='mt-4 gap-3 gap-lg-0'>
                        <Col lg={6}>
                            <div className="form-group">
                                <div className="d-flex flex-column">
                                    <label htmlFor="creators-count-range">Application Restriction Duration</label>
                                    <span className='field-desc'>{FIELDS_INSTRUCTIONS.CAMPAIGN.APPLICATION_RESTRICTION_DURATION}</span>
                                </div>
                                <select value={(isset(formFields.values.application_restriction_duration)) ? formFields.values.application_restriction_duration : 2}
                                    onChange={(e: any) => {
                                        formFields.setFieldValue('application_restriction_duration', !isNaN(Number(e.target.value)) ? Number(e.target.value) : 2);
                                    }}
                                    className="form-select">
                                    <option value="2">2 Weeks</option>
                                    <option value="4">4 Weeks</option>
                                    <option value="8">8 Weeks</option>
                                    <option value="12">12 Weeks</option>
                                    <option value="0">Forever</option>
                                </select>
                            </div>
                        </Col>
                    </Row>
                }

                {/* <div className="mt-2 d-flex justify-content-start gap-2">
                    <span>
                        <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1.3335 13V3C1.3335 1.89543 2.22892 1 3.33349 1H8.66683C9.7714 1 10.6668 1.89543 10.6668 3V13L7.08168 10.6953C6.42286 10.2717 5.57746 10.2717 4.91865 10.6953L1.3335 13Z" stroke="black" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </span>
                    <div className="d-flex flex-column gap-2">
                        <span className="lookalike-heading">Auto-invite creators to this campaign</span>
                        <span className='lookalike-subheading'>These creators will be invited after the brief is launched</span>
                    </div>
                </div> */}
            </div>
        </CampaignCreationLayout>
    )
}
export default withAccessControl(CampaignCreators, PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)